import React from 'react';

import CalendarIcon from '@hero/ui-kit/icons/branding/CalendarIcon';
import DeviceIcon from '@hero/ui-kit/icons/branding/DeviceIcon';
import StatusRejectedIcon from '@hero/ui-kit/icons/utility/StatusRejectedIcon';
import UserCareManagerIcon from '@hero/ui-kit/icons/utility/UserCareManagerIcon';
import UserIcon from '@hero/ui-kit/icons/utility/UserIcon';
import UsersIcon from '@hero/ui-kit/icons/utility/UsersIcon';
import CaretIcon from '@hero/ui-kit/icons/utility/CaretIcon';
import { useQueryClient } from '@tanstack/react-query';
import Filter from '../../../../components/Filter';
import CareManagerDropdown from '../../components/CareManagerDropdown';
import { useUserStatusContext } from '../../../../context/UserStatus';
import useIsCareManager from '../../../../hooks/useIsCareManager';
import { getBillingFormattedDate, getFormattedDate, parseToDate } from '../../../../utils/date';
import { ORGANIZATION_MEMBER_KEY } from '../../api/constants';
import useAssignCareManagers from '../../api/useAssignCareManagers';
import useGetCareManagers from '../../api/useGetCareManagers';
import { useOrganizationMemberDetailsContext } from '../context/OrganizationMemberDetailsContext';
import * as Style from './style.module.scss';

interface UserInfoProps {}

const CareManagerAvatar: React.FC<{
    careManager: { first_name: string; last_name: string; image_url: string } | null;
}> = ({ careManager }) => {
    if (careManager?.image_url) {
        return (
            <img
                src={careManager.image_url}
                alt={`${careManager.first_name} ${careManager.last_name}`}
                className={Style.careManagerAvatar}
            />
        );
    }
    return (
        <div className={Style.careManagerAvatarPlaceholder}>
            <UserCareManagerIcon size="small" />
        </div>
    );
};

const Item: React.FC<{ label: string; value: string; icon?: React.ReactElement }> = ({
    label,
    value,
    icon
}) => {
    return (
        <div>
            <div className={Style.userInfoLabel}>
                {icon}
                {label}:
            </div>

            <strong className={Style.userInfoValue}>{value}</strong>
        </div>
    );
};

const UserInfo: React.FC<UserInfoProps> = () => {
    const { memberDetails, currentDevice } = useOrganizationMemberDetailsContext();
    const { permissions } = useUserStatusContext();
    // HWM-23020: assigning/reassigning a member's Care Manager requires the Care Manager
    // module EDIT bit (Supervisor / Admin), NOT fleetMemberEdit. Hero Standard holds
    // fleetMemberEdit but not this bit, so it must not see the reassign dropdown.
    const canEditCareManager = permissions.careManager.careManagerEdit;
    // Front-line CMs hold careManagerEdit too but may not reassign, so they get the
    // read-only label instead. See useIsCareManager for why this can't be a bit check.
    const isCareManager = useIsCareManager();
    const { data: careManagers } = useGetCareManagers();
    const queryClient = useQueryClient();

    const { mutateAsync, isPending } = useAssignCareManagers(() => {
        queryClient.invalidateQueries({ queryKey: [ORGANIZATION_MEMBER_KEY] });
    });

    const activatedDate = currentDevice?.device_statuses?.find(
        (status) => status.device_status === 'ACTIVATED'
    )?.status_timestamp;

    const hasDevice = currentDevice?.device_status;

    const { member_info } = memberDetails;

    const handleReassign = React.useCallback(
        (careManagerId: number) => {
            if (!member_info.member_id) {
                return;
            }
            mutateAsync({
                payload: {
                    care_manager_ids: [careManagerId],
                    member_ids: [member_info.member_id]
                }
            });
        },
        [member_info.member_id, mutateAsync]
    );

    const assignedCareManagers =
        careManagers?.filter((cm) => memberDetails.member_info.care_manager_ids?.includes(cm.id)) ??
        [];

    const assignedCareManager = assignedCareManagers[0] ?? null;

    const careManagerLabel = assignedCareManagers.length
        ? assignedCareManagers.map((cm) => `${cm.first_name} ${cm.last_name}`).join(', ')
        : 'N/A';

    return (
        <>
            <div className={Style.userInfo}>
                {member_info?.external_user_id && (
                    <Item
                        icon={<UserIcon />}
                        label="Patient ID"
                        value={member_info.external_user_id}
                    />
                )}

                {canEditCareManager && !isCareManager && (
                    <div>
                        <div className={Style.userInfoLabel}>
                            <UserCareManagerIcon />
                            Care Manager:
                        </div>
                        {careManagers?.length ? (
                            <Filter
                                filterLabel={
                                    isPending ? (
                                        'Assigning...'
                                    ) : (
                                        <div className={Style.careManagerFilterLabel}>
                                            <CareManagerAvatar careManager={assignedCareManager} />
                                            <span>{careManagerLabel}</span>
                                            <CaretIcon size="small" direction="down" />
                                        </div>
                                    )
                                }
                                width="20rem"
                                minimal
                            >
                                <CareManagerDropdown
                                    careManagers={careManagers}
                                    onSelect={(cm) => handleReassign(cm.id)}
                                    disabled={isPending}
                                    selectedIds={
                                        memberDetails.member_info.care_manager_ids?.map(String) ??
                                        []
                                    }
                                />
                            </Filter>
                        ) : (
                            <div className={Style.careManagerCell}>
                                <CareManagerAvatar careManager={assignedCareManager} />
                                <strong className={Style.userInfoValue}>{careManagerLabel}</strong>
                            </div>
                        )}
                    </div>
                )}

                {isCareManager && (
                    <div>
                        <div className={Style.userInfoLabel}>
                            <UserCareManagerIcon />
                            Care Manager:
                        </div>
                        <div className={Style.careManagerCell}>
                            <CareManagerAvatar careManager={assignedCareManager} />
                            <strong className={Style.userInfoValue}>{careManagerLabel}</strong>
                        </div>
                    </div>
                )}

                {member_info.cohort_date && (
                    <Item
                        icon={<UsersIcon />}
                        label="Cohort"
                        value={getFormattedDate(parseToDate(member_info.cohort_date), 'MMM, yyyy')}
                    />
                )}

                {member_info?.hero_user_id && (
                    <Item
                        icon={<UserIcon type="brand" />}
                        label="Hero ID"
                        value={member_info.hero_user_id.toString()}
                    />
                )}

                {/* HWM-23225: not gated on `hasDevice` — a finalized discharge hides the device
                    status pill, leaving this as the only place the Device ID is shown. */}
                {memberDetails.owned_device_info?.device_serial && (
                    <Item
                        icon={<DeviceIcon type="brand" />}
                        label="Device ID"
                        value={memberDetails.owned_device_info.device_serial}
                    />
                )}

                {hasDevice && (
                    <div>
                        <Item
                            icon={<CalendarIcon type="utility" />}
                            label="Member since"
                            value={
                                activatedDate
                                    ? `${getBillingFormattedDate(activatedDate, 'MMM d, yyyy')}`
                                    : 'Not activated yet'
                            }
                        />
                    </div>
                )}

                {member_info.status_info?.discharged_at && (
                    <Item
                        icon={<StatusRejectedIcon />}
                        label="Discharged on"
                        value={getBillingFormattedDate(
                            member_info.status_info?.discharged_at,
                            'MMM d, yyyy'
                        )}
                    />
                )}

                {member_info.status_info?.cancelled_at && (
                    <Item
                        icon={<StatusRejectedIcon type="brand" />}
                        label="Discharge finalized on"
                        value={getBillingFormattedDate(
                            member_info.status_info?.cancelled_at,
                            'MMM d, yyyy'
                        )}
                    />
                )}
            </div>
        </>
    );
};

export default UserInfo;
